import { GetScopeSearchRequest, GetScopeValuesRequest, ScopeItem, TopologyService } from "../../Scopes.types";
import { getSelectedEnvironment, useSubstrateEnvironmentRingTopologyService } from "./useSubstrateEnvironmentRingTopologyService";

import { CentralAdminEntityType } from "Enums/CentralAdminEntityType";
import { Constants } from "Shared/Constants";
import { centralAdminFaultZoneSchema } from "Shared/Components/Scope/Topology/CentralAdminTopology";
import { useScopeSearch } from "../useScopeSearch";
import { useSubstrateRegion } from "./useSubstrateRegion";
import { useSubstrateForest } from "./useSubstrateForest";
import { useSubstrateFaultZone } from "./useSubstrateFaultZone";
import { useSubstrateCapacityUnit } from "./useSubstrateCapacityUnit";
import { useSubstrateFEMachine } from "./useSubstrateFEMachine";
import { useORPCClient } from "Core/oRPC";

export const useSubstrateCafeTopologyService = (): TopologyService => {
    const { inventoryApi } = useORPCClient();
    const envRingTopologyService = useSubstrateEnvironmentRingTopologyService();
    const { getScopeSearchResult } = useScopeSearch();
    const { getRegionList } = useSubstrateRegion();
    const { getForestList } = useSubstrateForest();
    const { getFaultZoneList } = useSubstrateFaultZone();
    const { getCapacityUnitList } = useSubstrateCapacityUnit();
    const { getFEMachineList } = useSubstrateFEMachine();
    return {
        getScopeValueOptions: async (request: GetScopeValuesRequest): Promise<ScopeItem[]> => {
            const currentScopeTarget = request.currentScopeTarget;
            const environment = getSelectedEnvironment(request.selectedScopesSoFar || []);
            const workload = request.context?.Workload ?? Constants.defaultWorkload;

            switch (currentScopeTarget.ScopeType.toLowerCase()) {
                case "region":
                    return getRegionList(environment, workload);
                case "location":
                    const selectedFaultZone = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "faultzone");
                    if (!selectedFaultZone || selectedFaultZone.Value?.toLowerCase() === "all") {
                        return [];
                    }
                    const locationRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.FaultZone,
                        TargetValue: selectedFaultZone.Value?.toUpperCase()
                    };

                    const locationOutput = await inventoryApi.postTopology(locationRequest);
                    const locationParsedOutput = centralAdminFaultZoneSchema.parse(locationOutput);
                    return locationParsedOutput.Locations?.map((item) => ({ ScopeType: "Location", Value: item })) ?? [];
                case "forest":
                    return getForestList(environment, workload, request.selectedScopesSoFar, false);
                case "faultzone":
                    return getFaultZoneList(environment, workload, request.selectedScopesSoFar);
                case "capacityunit":
                    return getCapacityUnitList(environment, workload, request.selectedScopesSoFar);
                case "machine":
                    return getFEMachineList(environment, workload, request.selectedScopesSoFar);
                default:
                    return envRingTopologyService.getScopeValueOptions(request);
            }
        },
        getScopeSearch: (request: GetScopeSearchRequest): Promise<ScopeItem[]> => {
            const validTargetTypes = [
                CentralAdminEntityType.Region,
                CentralAdminEntityType.Forest,
                CentralAdminEntityType.FaultZone,
                CentralAdminEntityType.CapacityUnit,
                CentralAdminEntityType.Machine
            ];
            return getScopeSearchResult(request, validTargetTypes);
        }
    };
};
